import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CreditCard, FileDown, MapPin, PackagePlus, RefreshCw, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import MonerisCheckoutModal from '@/components/document-services/MonerisCheckoutModal';
import OrderSummary from '@/components/document-services/OrderSummary';
import OrderTimeline from '@/components/document-services/OrderTimeline';
import { ServiceError, ServiceLoading, ServicePageHeader, ServiceStatusBadge } from '@/components/document-services/DocumentServicesCommon';
import { cancelMyServicePayment, getMyServiceOrder, startMyServiceCheckout, verifyMyServicePayment } from '@/lib/api';
import { apiErrorMessage, formatServiceDate, humanize, orderId, orderNumber, unwrapEntity } from '@/lib/documentServices';

function AnswerList({ answers }) {
  const rows = Object.entries(answers || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined);
  if (!rows.length) return <p className="text-sm text-[#062B1A]/50">No additional answers were recorded.</p>;
  const valueText = (value) => {
    if (typeof value === 'boolean') return value ? 'Accepted' : 'No';
    if (value && typeof value === 'object') return Object.values(value).filter(Boolean).join(', ');
    return String(value);
  };
  return <dl className="divide-y divide-[#062B1A]/8">{rows.map(([key, value]) => <div key={key} className="grid gap-1 py-3 sm:grid-cols-[210px_1fr]"><dt className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">{humanize(key)}</dt><dd className="text-sm text-[#062B1A]">{valueText(value)}</dd></div>)}</dl>;
}

export default function ServiceOrderDetail() {
  const { orderId: routeOrderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getMyServiceOrder(routeOrderId);
      const next = unwrapEntity(data, ['order']);
      setOrder({
        ...next,
        tracking: data?.tracking || next?.tracking,
        shipping_upgrade: data?.shipping_upgrade ?? next?.shipping_upgrade,
        fulfillment: {
          ...(next?.fulfillment || {}),
          ...(data?.tracking || next?.tracking || {}),
          recipient_name: next?.recipient?.name,
          address: next?.shipping_address,
        },
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'This order could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [routeOrderId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <ServiceLoading label="Loading order…" />;
  if (!order || error) return <ServiceError message={error || 'Order not found.'} onRetry={load} />;

  const id = orderId(order);
  const unpaid = ['pending', 'failed', 'cancelled'].includes(order.payment_status) && order.order_status !== 'cancelled';
  const canRetry = order.can_retry_payment === true || (unpaid && order.payment_status !== 'processing');
  const shipping = order.shipping || order.fulfillment || {};
  const answers = order.form_answers || order.answers || order.items?.[0]?.form_answers || {};
  const firstItem = order.items?.[0] || {};
  const shippingUpgrade = order.shipping_upgrade;
  const hasAuthoritativeUpgrade = typeof shippingUpgrade?.eligible === 'boolean';
  const looksUpgradeEligible = ['paid', 'partially_refunded'].includes(order.payment_status)
    && order.order_kind !== 'shipping_upgrade'
    && (firstItem.delivery_method === 'regular_mail' || firstItem.option_values?.delivery_method === 'regular_mail')
    && !['shipped', 'delivered', 'completed', 'unable_to_fulfill'].includes(order.fulfillment_status)
    && !['cancelled'].includes(order.order_status);
  const legacyUpgradeEligible = order.shipping_upgrade_eligible === true
    || order.add_on_eligibility?.shipping_upgrade === true;
  const upgradeEligible = hasAuthoritativeUpgrade
    ? shippingUpgrade.eligible
    : (legacyUpgradeEligible || looksUpgradeEligible);
  const upgradeTargets = shippingUpgrade?.code === 'parent_item_required'
    ? (order.items || []).filter((item) => (
      item?.delivery_method === 'regular_mail' || item?.option_values?.delivery_method === 'regular_mail'
    )).map((item) => ({ id: item.id, label: item.product_name || item.variant_name || 'Document' }))
    : upgradeEligible
      ? [{ id: shippingUpgrade?.parent_item_id || firstItem.id || '', label: '' }]
      : [];
  const receiptAvailable = ['paid', 'partially_refunded', 'refunded'].includes(order.payment_status);

  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader
        backTo="/student/orders"
        backLabel="My Orders"
        eyebrow={orderNumber(order)}
        title={order.items?.[0]?.product_name || order.product_name || 'Document service order'}
        description={`Ordered ${formatServiceDate(order.created_at, true)}`}
        actions={<><Button type="button" variant="outline" onClick={load} className="border-[#062B1A]/15 text-[#062B1A]"><RefreshCw className="mr-2 size-4" />Refresh</Button>{canRetry && <Button type="button" onClick={() => setPaymentOpen(true)} className="bg-[#062B1A] text-white"><CreditCard className="mr-2 size-4" />Pay securely</Button>}</>}
      />
      <div className="flex flex-wrap gap-2"><ServiceStatusBadge kind="order" status={order.order_status} /><ServiceStatusBadge kind="payment" status={order.payment_status} /><ServiceStatusBadge kind="fulfillment" status={order.fulfillment_status} />{order.accounting_sync_status && <ServiceStatusBadge kind="accounting" status={order.accounting_sync_status} />}</div>

      {order.payment_status === 'processing' || order.payment_status === 'review_required' ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Payment verification is still in progress. For double-charge protection, do not begin another payment attempt.</div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="text-base text-[#062B1A]">Processing timeline</CardTitle></CardHeader><CardContent><OrderTimeline events={order.timeline || order.fulfillment_timeline || order.audit_public} fallbackOrder={order} /></CardContent></Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="text-base text-[#062B1A]">Order information</CardTitle></CardHeader><CardContent><AnswerList answers={answers} /></CardContent></Card>
          {(shipping.recipient_name || shipping.address || shipping.tracking_number || order.fulfillment_type || upgradeTargets.length > 0) && (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="flex items-center gap-2 text-base text-[#062B1A]"><Truck className="size-4 text-[#D4AF37]" />Pickup or delivery</CardTitle></CardHeader><CardContent className="space-y-3 text-sm text-[#062B1A]/65">{shipping.recipient_name && <p><span className="font-medium text-[#062B1A]">Recipient:</span> {shipping.recipient_name}</p>}{shipping.address && <p className="flex items-start gap-2"><MapPin className="mt-0.5 size-4 shrink-0" />{typeof shipping.address === 'string' ? shipping.address : Object.values(shipping.address).filter(Boolean).join(', ')}</p>}{shipping.tracking_number && <p><span className="font-medium text-[#062B1A]">Tracking:</span> {shipping.tracking_number}</p>}<div className="flex flex-wrap gap-2">{(shipping.tracking_number || order.fulfillment_status === 'shipped') && <Button asChild size="sm" variant="outline"><Link to={`/student/orders/${encodeURIComponent(id)}/tracking`}>View tracking</Link></Button>}{upgradeTargets.map((target) => <Button key={target.id || 'upgrade'} asChild size="sm" variant="outline"><Link to={`/student/orders/${encodeURIComponent(id)}/shipping-upgrade${target.id ? `?parent_item_id=${encodeURIComponent(target.id)}` : ''}`}><PackagePlus className="mr-2 size-4" />{target.label ? `Add tracking: ${target.label}` : 'Add tracking'}</Link></Button>)}</div></CardContent></Card>
          )}
        </div>
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <OrderSummary order={order} />
          {receiptAvailable && <div className="grid grid-cols-2 gap-2"><Button asChild variant="outline" className="border-[#062B1A]/15 text-[#062B1A]"><Link to={`/student/orders/${encodeURIComponent(id)}/receipt`}><FileDown className="mr-2 size-4" />Receipt</Link></Button><Button asChild variant="outline" className="border-[#062B1A]/15 text-[#062B1A]"><Link to={`/student/orders/${encodeURIComponent(id)}/receipt?document=invoice`}>Invoice</Link></Button></div>}
          {order.parent_order && <Link to={`/student/orders/${encodeURIComponent(orderId(order.parent_order))}`} className="block rounded-xl border border-[#062B1A]/10 bg-white p-4 text-sm text-[#062B1A] hover:border-[#D4AF37]">Linked to parent order <span className="font-semibold">{orderNumber(order.parent_order)}</span></Link>}
          {order.shipping_upgrade_order && <Link to={`/student/orders/${encodeURIComponent(orderId(order.shipping_upgrade_order))}`} className="block rounded-xl border border-[#062B1A]/10 bg-white p-4 text-sm text-[#062B1A] hover:border-[#D4AF37]">Shipping upgrade <span className="font-semibold">{orderNumber(order.shipping_upgrade_order)}</span></Link>}
        </aside>
      </div>

      <MonerisCheckoutModal
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        orderId={id}
        idempotencyKey={order.checkout_idempotency_key || order.payment_attempt?.idempotency_key || ''}
        startCheckout={startMyServiceCheckout}
        verifyPayment={verifyMyServicePayment}
        cancelPayment={cancelMyServicePayment}
        onVerified={(result) => navigate(`/student/orders/${encodeURIComponent(id)}/payment-result`, { state: { paymentResult: result } })}
      />
    </div>
  );
}
